// frontend/src/pages/Portal/SpecialistProfile/state/SpecialistProfileValidation.ts

import {
  SpecialistProfileFormState,
  BasicProfileErrors,
  ClinicalProfileErrors,
  AccessErrors,
  HospitalsProceduresErrors,
  ReferralErrors,
} from "./SpecialistProfileTypes";

export const validateBasicProfile = (
  state: SpecialistProfileFormState
): BasicProfileErrors => {
  const errors: BasicProfileErrors = {};
  const {
    userId,
    cpsNumber,
    fullName,
    gender,
    clinicId,
    acceptingNewReferrals,
  } = state.basicProfile;

  if (!userId.trim()) errors.userId = "User link is required (temporary field).";
  if (!cpsNumber.trim()) errors.cpsNumber = "CPSO number is required.";
  if (!fullName.trim()) errors.fullName = "Full name is required.";
  if (!gender) errors.gender = "Please select a gender.";
  if (!clinicId) errors.clinicId = "Please select a primary clinic.";
  if (!acceptingNewReferrals)
    errors.acceptingNewReferrals = "Please specify if you accept new referrals.";

  return errors;
};

export const validateClinicalProfile = (
  state: SpecialistProfileFormState
): ClinicalProfileErrors => {
  const errors: ClinicalProfileErrors = {};
  const { specialtyId, ageGroups } = state.clinicalProfile;

  if (!specialtyId) errors.specialtyId = "Please select a primary specialty.";
  if (!ageGroups || ageGroups.length === 0)
    errors.ageGroups = "Please select at least one age group you serve.";

  return errors;
};

export const validateAccess = (state: SpecialistProfileFormState): AccessErrors => {
  const errors: AccessErrors = {};
  const { languageIds, visitTypeIds, coverageTypeIds } = state.access;

  if (!languageIds || languageIds.length === 0)
    errors.languageIds = "Please select at least one language.";
  if (!visitTypeIds || visitTypeIds.length === 0)
    errors.visitTypeIds = "Please select at least one visit type.";
  if (!coverageTypeIds || coverageTypeIds.length === 0)
    errors.coverageTypeIds = "Please select at least one coverage type.";

  return errors;
};

export const validateHospitalsProcedures = (
  state: SpecialistProfileFormState
): HospitalsProceduresErrors => {
  const errors: HospitalsProceduresErrors = {};
  const { hospitalIds, hasNoHospitalPrivileges } = state.hospitalsAndProcedures;

  if (!hasNoHospitalPrivileges && (!hospitalIds || hospitalIds.length === 0)) {
    errors.hospitalIds =
      "Please select at least one hospital or check 'No hospital privileges'.";
  }

  return errors;
};

export const validateConditions = (_state: SpecialistProfileFormState): void => {
  // Optional for now
};

const validateOptionalNonNegativeNumber = (
  raw: string,
  label: string
): string | null => {
  const t = (raw ?? "").trim();
  if (!t) return null; // optional
  const n = Number(t);
  if (Number.isNaN(n) || n < 0) return `${label} must be a non-negative number.`;
  return null;
};

/**
 * Tab 6: Referral & Wait Times
 */
export const validateReferral = (
  state: SpecialistProfileFormState
): ReferralErrors => {
  const errors: ReferralErrors = {};
  const { routineWaitWeeks, urgentWaitWeeks, referralMethodCodes } = state.referral;

  // Referral method is required (UI has "*")
  if (!Array.isArray(referralMethodCodes) || referralMethodCodes.length === 0) {
    errors.referralMethodCodes = "Please select at least one referral method.";
  }

  // Wait times are optional (UI says "Leave blank if unknown")
  const routineErr = validateOptionalNonNegativeNumber(
    routineWaitWeeks ?? "",
    "Routine wait time"
  );
  if (routineErr) errors.routineWaitWeeks = routineErr;

  const urgentErr = validateOptionalNonNegativeNumber(
    urgentWaitWeeks ?? "",
    "Urgent wait time"
  );
  if (urgentErr) errors.urgentWaitWeeks = urgentErr;

  return errors;
};
